import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, useApi } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { setPendingReferral, clearPendingReferral } from '../lib/pendingReferral';
import '../Dashboard.css';

// Public landing page for personal/profile invite links: sphere-sync.com/r/<CODE>.
// Unlike a circle invite, this just brings a new person into the app — redeeming it credits
// the inviter's Invite badge (only for a brand-new, verified account). Anyone can open it; it
// previews who invited them, then routes to sign up / sign in, or redeems when already signed in.
export default function JoinReferral() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const cleanCode = String(code || '').trim().toUpperCase();

  const { data, loading, error } = useApi(`/auth/invite/${encodeURIComponent(cleanCode)}`);
  const [redeeming, setRedeeming] = useState(false);

  const invalid = !loading && (error || !data?.valid);
  const inviter = data?.inviter?.displayName;
  const authed = !authLoading && user && user.emailVerified;

  // Survive the whole sign-up journey (register → verify → sign-in, possibly a new tab):
  // the dashboard redeems the stashed code on arrival. Cleared on an invalid code.
  useEffect(() => {
    if (cleanCode) setPendingReferral(cleanCode);
  }, [cleanCode]);
  useEffect(() => {
    if (invalid) clearPendingReferral();
  }, [invalid]);

  // Signed-in visitors redeem immediately — the link IS the action. The backend only credits a
  // genuinely new + verified account, so an existing user simply lands in the app.
  useEffect(() => {
    if (!authed || !data?.valid || redeeming) return;
    setRedeeming(true);
    api.post('/auth/invite/redeem', { code: cleanCode })
      .catch(() => {})
      .finally(() => {
        clearPendingReferral();
        navigate('/app', { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, data?.valid]);

  // After auth, land straight in /app — the code is stashed in localStorage (setPendingReferral) and
  // redeemed by DashboardLayout, so we don't bounce the user back through this invite page (which is
  // what made returning users feel stuck in a "you're invited" loop).
  const loginState = { state: { from: { pathname: '/app' } } };

  return (
    <div className="db-auth-page">
      <div className="db-auth-bg" aria-hidden="true">
        <span className="db-glow db-glow-a" />
        <span className="db-glow db-glow-b" />
      </div>
      <div className="db-auth-card">
        <Link to="/" className="db-auth-brand">
          <img className="db-mark" src="/spheresync-icon.png" alt="" aria-hidden="true" />
          Sphere<span className="db-grad">Sync</span>
        </Link>

        {loading || authLoading ? (
          <p className="db-auth-sub">Checking your invite…</p>
        ) : invalid ? (
          <>
            <h1 className="db-auth-title">Invite link is invalid</h1>
            <p className="db-auth-sub">
              This invite link is no longer valid. Ask whoever sent it for a fresh link.
            </p>
            <Link to="/" className="db-btn db-btn-block" style={{ textAlign: 'center' }}>Back to SphereSync</Link>
          </>
        ) : authed ? (
          <p className="db-auth-sub">Taking you into SphereSync…</p>
        ) : (
          <>
            <h1 className="db-auth-title">
              {inviter ? `${inviter} invited you to SphereSync` : 'You’re invited to SphereSync'}
            </h1>
            {/* person-framed pitch (MARKETING.md §2.5) — the inviter is the whole reason they're here */}
            <p className="db-auth-sub">
              SphereSync is the private place where friends plan moments together and keep the
              memories. Create your account and you&rsquo;ll be connected to
              {inviter ? ` ${inviter}` : ' your friend'} from day one.
            </p>

            <Link to="/register" {...loginState} className="db-btn db-btn-primary db-btn-block" style={{ textAlign: 'center' }}>
              Create account
            </Link>
            <Link to="/login" {...loginState} className="db-btn db-btn-block" style={{ textAlign: 'center', marginTop: 10 }}>
              I already have an account
            </Link>

            <p className="db-auth-foot">
              Have the Android app? <a href={`spheresync://r/${cleanCode}`}>Open this invite in the app</a>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
